import { useCartDrawer } from '@iheartjane/cart';
import { useBrandSpecialQualifyingStatus } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  SpecialDetailLayout,
  SpecialDetailStatus,
  extractUserSegments,
  getDiscountedWithoutSameQualified,
  getSpecialType,
} from '@iheartjane/menu';
import { Box, Flex, useMobileMediaQuery } from '@iheartjane/reefer';
import type { Breadcrumb, PriceId } from '@iheartjane/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import {
  JaneGoldCallout,
  MenuBreadcrumbs,
  UnavailableBrandSpecial,
} from '@jane/shared-ecomm/components';
import { useIsUserInSegment } from '@jane/shared-ecomm/hooks';
import {
  useAuthentication,
  useBrandSpecialsContext,
  useMenu,
} from '@jane/shared-ecomm/providers';
import { useJaneUser } from '@jane/shared/data-access';
import { useNeedsSignInForEligibility } from '@jane/shared/hooks';
/* eslint-disable-next-line no-restricted-imports */
import type { PendingCartProduct } from '@jane/shared/models';
import { useEcommApp } from '@jane/shared/providers';
import { insertIfArr } from '@jane/shared/util';

import { useComposeMenuFilters } from '../../hooks/useComposeMenuFilters';
import { MenuFiltersAndProductsGrid } from '../../MenuComponents/MenuFiltersAndProductsGrid/MenuFiltersAndProductsGrid';
import { MenuPageSeo } from '../../MenuComponents/MenuSeo/MenuPageSeo';
import { MenuTitle } from '../../MenuComponents/MenuTitle/MenuTitle';
import { getFilterForSpecialType } from './MenuBrandSpecialDetail.util';
import { useStatusText } from './useStatusText';
import { useWeightFilters } from './useWeightFilters';

export const MenuBrandSpecialDetail = () => {
  const prevStatusRef = useRef<string | undefined>(undefined);
  const isMobile = useMobileMediaQuery({});
  const { state } = useLocation();
  const { appMode } = useEcommApp();
  const extendedDealsLabelsEnabled = useFlag(FLAGS.c1ExtendedDealsLabels);
  const { brand_special_id: specialId } = useParams<{
    brand_special_id?: string;
    store_id?: string;
  }>();

  const {
    cart,
    dispatches: { deleteCartItem, updateCart },
    paths,
    store,
  } = useMenu();
  const { open: openCart } = useCartDrawer();
  const cartStoreId = cart.store?.id;
  const atCorrectStore =
    !cartStoreId || String(cartStoreId) === String(store.id);

  const {
    defaultLogin,
    errors,
    onAppleClick,
    onGoogleClick,
    register,
    resetError,
    resetPassword,
    showAppleLogin,
    showGoogleLogin,
  } = useAuthentication();

  const authentication = {
    errors,
    login: defaultLogin,
    onAppleClick,
    onGoogleClick,
    register,
    resetError,
    resetPassword,
    showAppleLogin,
    showGoogleLogin,
  };

  const { brandSpecials, loadingBrandSpecials } = useBrandSpecialsContext();

  const currentSpecial = brandSpecials.find(
    (special) => String(special.id) === specialId
  );
  const isProductSpecial = currentSpecial?.special_type === 'product';

  const needsSignInForEligibility = useNeedsSignInForEligibility({
    brandSpecial: currentSpecial,
  });

  const { includes, excludes } = extractUserSegments({
    rules: currentSpecial?.rules || {},
    specialType: currentSpecial?.special_type || 'product',
  });

  const { data: janeUser } = useJaneUser();

  const userIsEligible = useIsUserInSegment({
    userSegmentIds: janeUser?.user_segments || [],
    enableCheck: Boolean(currentSpecial && janeUser?.user.authenticated),
    excludedIds: excludes,
    includedIds: includes,
  });
  const isEligible = useMemo(
    () => userIsEligible && !!currentSpecial,
    [userIsEligible, currentSpecial]
  );

  const pageTitle = currentSpecial?.title || '';

  const { data: qualifyingStatusData, refetch: fetchStatus } =
    useBrandSpecialQualifyingStatus(
      {
        cartUuid: cart.uuid,
        specialId,
        storeId: store.id,
      },
      { enabled: atCorrectStore }
    );

  const requiredEligibility = Boolean(
    !janeUser?.user?.authenticated && (!!includes?.length || !!excludes?.length)
  );

  const [qualifyingStatus, setQualifyingStatus] = useState(
    qualifyingStatusData?.qualifying_status
  );

  const {
    calloutSubtext,
    calloutText,
    footerSubtext,
    footerText,
    stickyCallout,
    stickyCalloutSubtext,
    stickySubtext,
    stickyText,
  } = useStatusText({
    currentSpecial,
    qualifyingStatus: qualifyingStatusData,
    requiredEligibility,
  });

  const { additionalProductFilter, weightFilter } = useWeightFilters({
    cartProducts: cart.products as PendingCartProduct[],
    currentSpecial,
    qualifyingStatus,
  });

  const scrollToStatus = () =>
    (document.getElementById('shadow-host')?.shadowRoot || document)
      .getElementById('jane-menu-header')
      ?.scrollIntoView({ behavior: 'smooth' });

  const handleRemoveProduct = (product: {
    id?: number;
    price_id?: PriceId;
  }) => {
    const cartProduct = cart.products.find(
      ({ id, price_id }) => id === product.id && price_id === product.price_id
    );

    if (!cartProduct) return;

    if (cartProduct.count === 1) {
      deleteCartItem(cartProduct.id, cartProduct.price_id);
    } else {
      updateCart(cartProduct.id, cartProduct.price_id, cartProduct.count - 1);
    }
  };

  const discountedWithoutSameQualified =
    getDiscountedWithoutSameQualified(qualifyingStatusData);

  const discountedProductsToShow =
    currentSpecial?.special_type === 'buy_x_and_save'
      ? discountedWithoutSameQualified
      : qualifyingStatusData?.discounted_products;

  const specialFilter = getFilterForSpecialType(
    currentSpecial?.special_type,
    qualifyingStatus
  );

  const staticFilters = useComposeMenuFilters([
    `applicable_${specialFilter}:${currentSpecial?.id}`,
  ]);

  const fromFeatured = state?.breadcrumbs?.some(
    (crumb: Breadcrumb) => crumb.title === 'Featured'
  );

  const backLink = fromFeatured
    ? paths.menuCategory('featured')
    : paths.menuCategory('jane-gold');

  const specialDetailPath = paths.menuBrandSpecial({
    id: currentSpecial?.id || 0,
    title: pageTitle,
  });

  const breadcrumbs = [
    { url: '/', title: 'Home' },
    { url: paths.menu(), title: store.name },
    ...insertIfArr(!fromFeatured, {
      url: paths.menuCategory('jane-gold'),
      title: 'Jane Gold',
    }),
  ];

  const nextBreadcrumbs = [
    ...insertIfArr(!fromFeatured, {
      url: paths.menuCategory('jane-gold'),
      title: 'Jane Gold',
    }),
    ...insertIfArr(fromFeatured, {
      url: paths.menuCategory('featured'),
      title: 'Featured',
    }),
    {
      title: pageTitle,
      url: specialDetailPath,
    },
  ];

  useEffect(() => {
    if (cart.uuid && atCorrectStore) {
      fetchStatus();
    }
  }, [cart?.uuid, cart?.products, atCorrectStore, fetchStatus]);

  // When there is no cart uuid, the qualifying status fetch is not enabled, so
  // we need to manually set the status to 'not_qualified'
  useEffect(() => {
    if (!cart.uuid) {
      setQualifyingStatus('not_qualified');
    } else if (qualifyingStatusData?.qualifying_status) {
      setQualifyingStatus(qualifyingStatusData.qualifying_status);
    }
  }, [cart.uuid, qualifyingStatusData]);

  // We open the cart if the user reaches applied status while on the page,
  // but not if they were already at applied status when they navigated to the page
  useEffect(() => {
    if (
      !!currentSpecial &&
      !isProductSpecial &&
      qualifyingStatus === 'applied' &&
      !!prevStatusRef.current &&
      prevStatusRef.current !== 'applied'
    ) {
      openCart();
    }
    prevStatusRef.current = qualifyingStatus;
  }, [qualifyingStatus, currentSpecial, openCart, prevStatusRef]);

  return (
    <Box position="relative">
      <MenuPageSeo title={pageTitle} path={specialDetailPath} />
      <Flex
        flexDirection="column"
        px={isMobile ? 24 : 64}
        pb={isMobile ? 24 : 48}
        gap={isMobile ? 24 : 48}
      >
        {!isMobile && (
          <MenuBreadcrumbs breadcrumbs={breadcrumbs} title={pageTitle} />
        )}

        <JaneGoldCallout
          variant="goldRewards"
          authentication={authentication}
          segmentedDeal={needsSignInForEligibility}
          storeState={store.state}
        />
      </Flex>

      {!isEligible && (
        <UnavailableBrandSpecial
          buttonLabel={
            fromFeatured ? 'Back to menu' : 'Back to Jane Gold rewards'
          }
          backLink={backLink}
          message="Sorry, you are not eligible for this Jane Gold reward. Return to the menu to explore more cash back options."
        />
      )}

      {isEligible && (
        <SpecialDetailLayout
          px={!isMobile ? 64 : 0}
          isLoading={!!loadingBrandSpecials}
        >
          <SpecialDetailLayout.Banner p={8}>
            <SpecialDetailStatus.Sticky
              calloutText={stickyCallout}
              calloutSubtext={stickyCalloutSubtext}
              isGold
              subtext={stickySubtext}
              text={stickyText}
              qualifyingStatus={qualifyingStatus}
              onClick={scrollToStatus}
            />
          </SpecialDetailLayout.Banner>

          <SpecialDetailLayout.Summary
            px={isMobile ? 24 : 0}
            headerHeight={appMode === 'default' ? 80 : 0}
          >
            <SpecialDetailStatus>
              <SpecialDetailStatus.Header
                headerImageSrc={
                  currentSpecial?.photo?.urls?.original ?? undefined
                }
                specialDescription={currentSpecial?.description || ''}
                specialTitle={currentSpecial?.title || ''}
                specialType={
                  currentSpecial
                    ? getSpecialType(
                        currentSpecial.special_type === 'product'
                          ? 'product'
                          : 'bundle',
                        store?.store_compliance_language_settings,
                        extendedDealsLabelsEnabled
                      )
                    : ''
                }
                brandImage={currentSpecial?.product_brand.logo_url || ''}
                brandName={currentSpecial?.product_brand.name}
                specialTerms={currentSpecial?.terms || ''}
              />
              {!isProductSpecial && (
                <SpecialDetailStatus.BundleProgress
                  allowQualifiersToBeDiscounted={
                    currentSpecial?.special_type === 'buy_x_and_save'
                  }
                  calloutSubtext={calloutSubtext}
                  calloutText={calloutText || ''}
                  discountedProducts={discountedProductsToShow || []}
                  isGold
                  onRemoveProduct={(product) => handleRemoveProduct(product)}
                  qualifiedProducts={
                    qualifyingStatusData?.qualified_products || []
                  }
                  qualifyingStatus={qualifyingStatus || 'not_qualified'}
                  tagLabel={currentSpecial?.discount_label || ''}
                />
              )}
              {!isProductSpecial && (
                <SpecialDetailStatus.Footer
                  footerText={footerText}
                  footerSubtext={footerSubtext}
                  isGold
                  onCheckoutClick={() => openCart()}
                  qualifyingStatus={qualifyingStatus || 'not_qualified'}
                />
              )}
            </SpecialDetailStatus>
          </SpecialDetailLayout.Summary>

          <SpecialDetailLayout.Content px={isMobile ? 24 : 0}>
            <MenuTitle variant="gold" />

            <MenuFiltersAndProductsGrid
              additionalProductFilter={additionalProductFilter}
              weightFilter={weightFilter}
              breadcrumbs={nextBreadcrumbs}
              menuCategory="jane-gold"
              noPadding
              pageTitle={pageTitle}
              staticFilters={staticFilters}
              withoutDrawerOnAddToCart={!isProductSpecial}
            />
          </SpecialDetailLayout.Content>
        </SpecialDetailLayout>
      )}
    </Box>
  );
};
